import { clone } from '@sama/micro-dash';
import { debounce } from 'rxjs';
import { delayWhen } from 'rxjs/operators';
import { OrthographicShapeState2D } from '../../core/outputs/annotation/annotation-shared/fusion/projection/orthographic-orchestrator';
import { ShapeState } from '../../cross-team-shared/annotation/shared/state/shape-state';
import { pointCloudOrthographicShapeTypes } from '../../team-apis/otterdam/answers/point-cloud-answer';
import { Point2D } from '../../team-apis/otterdam/answers/shape-typings';

/**
 * Delays an observable on the micro queue (so no `runTimeouts()` is needed in
 * tests), and debounces them so that multiple emissions during a single turn
 * of the micro queue only end up emitting their last value.
 */
export function debounceOnMicroQueue<T>() {
  // previously we would use debounceTime<T>(0, asapScheduler), but there is an
  // issue with that scheduler. See
  // https://github.com/ReactiveX/rxjs/issues/6747
  return debounce<T>(() => Promise.resolve());
}

/**
 * Delays the emission of items from the source Observable using the microtask queue.
 */
export function delayOnMicrotaskQueue<T>() {
  // previously we would use delay<T>(0, asapScheduler), but there is an
  // issue with asapScheduler. See
  // https://github.com/ReactiveX/rxjs/issues/6747
  return delayWhen<T>(() => Promise.resolve());
}

export function isOrthographicShapeState2D(
  shape: ShapeState,
): shape is OrthographicShapeState2D {
  const pointCloudType = [...pointCloudOrthographicShapeTypes] as string[];
  return pointCloudType.includes(shape.type);
}

export function getPagePoint(element: HTMLElement): Point2D {
  const rect = element.getBoundingClientRect();
  return [rect.left + window.pageXOffset, rect.top + window.pageYOffset];
}

export function logBase(base: number, power: number) {
  return Math.log(power) / Math.log(base);
}

export function withInsertedElements<T>(
  array: T[],
  index: number,
  ...elements: T[]
) {
  // fix me if you change this code
  // eslint-disable-next-line no-param-reassign -- fix me!
  array = clone(array);
  array.splice(index, 0, ...elements);
  return array;
}

type Nil = null | undefined;

/**
 * Like `pick()` from micro-dash, but does not include keys that do not exist in
 * `object`. (`pick()` will include the key, with the value `undefined`.)
 *
 * Modified from https://github.com/simontonsoftware/micro-dash/blob/master/projects/micro-dash/src/lib/object/pick.ts
 */
export function pickExisting<T, K extends keyof T>(
  object: T | Nil,
  ...paths: K[]
): T extends Nil ? StrictRecord<string, never> : Pick<T, K> {
  const result: any = {};
  if (object != null) {
    for (const path of paths) {
      if (object.hasOwnProperty(path)) {
        result[path] = object[path];
      }
    }
  }
  return result;
}

// https://stackoverflow.com/a/48953930/1836506
export type PublicPart<T> = { [K in keyof T]: T[K] };
